import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { attachmentStageLabelKey, type TicketAttachment } from '@nbh/shared';
import { AttachmentImage } from '../ui/AttachmentImage';
import { altKeyFor, attachmentCaption } from '../../lib/attachments';

export interface PhotoLightboxProps {
  /** Photos only — a voice note has nothing to show full screen. */
  photos: TicketAttachment[];
  /** Which one was tapped. */
  startIndex?: number;
  onClose: () => void;
}

/**
 * One photo, full screen, and a way back out.
 *
 * Portalled to the body for the reason ProofSheet documents at its own render: the
 * action toolbar is `position: sticky`, which creates a stacking context unconditionally,
 * so anything rendered inside it sits under the fixed tab bar however high its z-index
 * goes — measured, not guessed, when `elementFromPoint` over a button returned a tab-bar
 * icon. The detail header is not sticky today, but a viewer opened from the header and
 * one opened from the gallery must behave identically, and the gallery sits above that
 * toolbar.
 *
 * Dependency-free on purpose: a carousel library is a payload on a 3G handset, and this
 * needs a picture, a caption and a close button.
 */
export function PhotoLightbox({ photos, startIndex = 0, onClose }: PhotoLightboxProps) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(startIndex);

  // A hardware or on-screen Escape closes it. Cheap, and the only way out for a manager
  // reading this on a desktop who never sees a tap target.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const photo = photos[Math.min(index, photos.length - 1)];
  if (!photo) return null;

  const step = (by: number) => setIndex((at) => (at + by + photos.length) % photos.length);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('attachment.viewer')}
      className="fixed inset-0 z-50 flex flex-col bg-black/95 pb-[var(--safe-bottom)] pl-[var(--safe-left)] pr-[var(--safe-right)] pt-[var(--safe-top)]"
    >
      <header className="flex items-center justify-between p-2">
        <p className="px-2 text-xs text-slate-400">
          {photos.length > 1 &&
            t('attachment.position', { index: index + 1, total: photos.length })}
        </p>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('attachment.close')}
          className="flex size-12 items-center justify-center bg-transparent p-0 text-slate-200"
        >
          <X className="size-6" aria-hidden="true" />
        </button>
      </header>

      <div className="flex flex-1 items-center justify-center overflow-hidden px-2">
        <AttachmentImage
          src={photo.url}
          alt={t(altKeyFor(photo.stage))}
          width={photo.width}
          height={photo.height}
          className="max-h-full max-w-full object-contain"
        />
      </div>

      <footer className="flex items-center justify-between gap-2 p-3">
        {photos.length > 1 && <StepButton direction="previous" onClick={() => step(-1)} />}

        <p className="min-w-0 flex-1 text-center text-xs text-slate-300">
          {photo.stage && (
            <span className="mr-1 font-semibold">{t(attachmentStageLabelKey[photo.stage])}</span>
          )}
          {attachmentCaption(photo, t)}
        </p>

        {photos.length > 1 && <StepButton direction="next" onClick={() => step(1)} />}
      </footer>
    </div>,
    document.body,
  );
}

/** Paging, at a size a gloved thumb hits: 48px square, per CLAUDE.md. */
function StepButton({
  direction,
  onClick,
}: {
  direction: 'previous' | 'next';
  onClick: () => void;
}) {
  const { t } = useTranslation();
  const Icon = direction === 'previous' ? ChevronLeft : ChevronRight;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={t(`attachment.${direction}`)}
      className="flex size-12 shrink-0 items-center justify-center rounded-full bg-slate-800 p-0 text-slate-100"
    >
      <Icon className="size-6" aria-hidden="true" />
    </button>
  );
}
